import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useLogin } from '../../context/LoginContext';
import { useModal } from '../../context/ModalContext';
import anchorService from '../../services/anchorService';
import { fetchSSOLoginFlows, buildSSORedirectUrl } from '../../services/ssoService';
import { isEmailAddress, parseMatrixId } from '../../services/matrixAuthService';
import './LoginForm.css';
import logger from '../../utils/logger';

// Helper function to validate username format
function isValidUsernameFormat(username) {
  if (!username || typeof username !== 'string') return false;
  const trimmed = username.trim();
  if (!trimmed) return false;
  
  // Check for spaces (invalid)
  if (/\s/.test(trimmed)) return false;
  
  // Accept email addresses, full Matrix IDs, or simple localpart usernames
  // Basically anything without spaces is valid
  return true;
}

const usernameSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#9b9b9b" height="24" width="24" size="24" color="rgb(224,224,224)">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
  </svg>
);

const passwordSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#9b9b9b" width="22px" height="22px" size="24" color="rgb(224,224,224)">
    <path d="M0 0h24v24H0z" fill="none"></path>
    <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"></path>
  </svg>
);

const LoginForm = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { login } = useLogin();
  const { showError } = useModal();

  const [step, setStep] = useState('username'); // 'username' | 'password'
  const [resolvedAnchorData, setResolvedAnchorData] = useState(null);
  const anchorAbortRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Real-time validation for username format
  const [usernameFormatError, setUsernameFormatError] = useState('');

  // Abort in-flight anchor request on unmount
  useEffect(() => {
    return () => {
      anchorAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (step === 'username' && formData.username.trim()) {
      if (!isValidUsernameFormat(formData.username)) {
        setUsernameFormatError(t('LoginPage.invalidUsernameFormat'));
      } else {
        setUsernameFormatError('');
      }
    } else {
      setUsernameFormatError('');
    }
  }, [formData.username, step, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear field-specific error when user types (functional updater avoids stale closure)
    setErrors(prev => {
      if (prev[name]) {
        return { ...prev, [name]: '' };
      }
      return prev;
    });
  };

  const handleUsernameContinue = async (e) => {
    e.preventDefault();
    const trimmedUsername = formData.username.trim();

    if (!trimmedUsername || !isValidUsernameFormat(trimmedUsername)) {
      return;
    }

    setFormData(prev => ({ ...prev, username: trimmedUsername }));

    // Check for fully-qualified Matrix ID (e.g. @user:server.example.com)
    const matrixId = parseMatrixId(trimmedUsername);
    if (matrixId) {
      logger.debug('Login: Detected full Matrix ID, using server:', matrixId.serverName);
      setResolvedAnchorData({ matrixServerName: matrixId.serverName });
      setErrors({});
      setUsernameFormatError('');
      setStep('password');
      return;
    }

    if (isEmailAddress(trimmedUsername)) {
      setIsLoading(true);
      setErrors({});
      setUsernameFormatError('');

      // Abort any previous in-flight request
      anchorAbortRef.current?.abort();
      const abortController = new AbortController();
      anchorAbortRef.current = abortController;

      try {
        const anchorData = await anchorService.resolveEmail(trimmedUsername, {
          signal: abortController.signal,
        });

        if (anchorData.type === 'SSO') {
          // Fetch login flows from the anchor's Matrix server
          const homeserverUrl = `https://${anchorData.matrixServerName}`;
          const { identityProviders } = await fetchSSOLoginFlows(homeserverUrl);

          // Match idpName to identity provider (case-insensitive)
          const idpNameLower = anchorData.idpName.toLowerCase();
          const provider = identityProviders.find(p => p.name.toLowerCase() === idpNameLower);
          if (!provider) {
            showError(
              t('LoginPage.anchorUnsupportedTypeTitle'),
              t('LoginPage.ssoProviderNotFound'),
            );
            return;
          }

          // Persist the login email so UIA re-authentication can pass it as login_hint
          try { localStorage.setItem('ssoLoginEmail', trimmedUsername); } catch { /* best-effort */ }

          // Redirect to SSO
          const ssoUrl = buildSSORedirectUrl(provider.id, { homeserverUrl, loginHint: trimmedUsername });
          // snyk:ignore:Open Redirect -- URL is from trusted SSO provider discovery response
          window.location.href = ssoUrl;
          return;
        }

        if (anchorData.type === 'Not Found') {
          // Proceed to password step — login will fail naturally
          setResolvedAnchorData(null);
          setStep('password');
          return;
        }

        if (anchorData.type === 'local') {
          setResolvedAnchorData(anchorData);
          setFormData(prev => ({ ...prev, username: anchorData.matrixId }));
          setStep('password');
          return;
        }

        // Unknown type — forward-compat error
        showError(
          t('LoginPage.anchorUnsupportedTypeTitle'),
          t('LoginPage.anchorUnsupportedTypeMessage', { type: anchorData.type }),
        );
      } catch (error) {
        if (error.name === 'AbortError') return;
        logger.error('Anchor resolution failed:', error);

        // Timeout or network failure → show inline error (infrastructure problem, no enumeration risk)
        if (error.message.includes('timed out') || error instanceof TypeError) {
          setErrors({ username: t('LoginPage.networkCommunicationError') });
          return;
        }

        // SSO login flow fetch failure
        if (error.message.includes('Failed to fetch login flows')) {
          showError(
            t('LoginPage.anchorUnsupportedTypeTitle'),
            t('LoginPage.ssoLoginFlowError'),
          );
          return;
        }

        // HTTP errors / malformed responses → silent fallthrough to prevent enumeration
        setStep('password');
      } finally {
        if (anchorAbortRef.current === abortController) {
          setIsLoading(false);
        }
      }
    } else {
      setResolvedAnchorData(null);
      setErrors({});
      setUsernameFormatError('');
      setStep('password');
    }
  };

  const handleBack = () => {
    // Abort any in-flight anchor request
    anchorAbortRef.current?.abort();
    // Clear password, resolved data, and all errors when going back
    setFormData(prev => ({ ...prev, password: '' }));
    setResolvedAnchorData(null);
    setErrors({});
    setStep('username');
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!formData.password) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const loginOptions = resolvedAnchorData
        ? { homeserver: `https://${resolvedAnchorData.matrixServerName}` }
        : {};
      const { success } = await login(formData, loginOptions);
      if (success) {
        // Credentials are already persisted inside matrixAuthService.login()
        // (via finalizeLogin -> storeCredentials -> credentialStore). The prior
        // explicit localStorage.setItem('matrixCreds', ...) here was redundant
        // (F-04) and bypassed the single credential-store owner — removed.
        navigate('/conversations');
      } else {
        setErrors({ password: t('LoginPage.invalidCredentials') });
      }
    } catch (error) {
      logger.error('Login failed:', error);
      setErrors({ password: t('LoginPage.invalidCredentials') });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-form-wrapper">
      <img className="login-form-logo" src="images/netsfereLogoWithText.png" alt="Netsfere Logo" />
      <div className="login-form-header">{t('LoginPage.loginToYourAccount')}</div>
      
      {step === 'username' ? (
        <form className="login-form" onSubmit={handleUsernameContinue}>
          <div className="login-form-group">
            <label htmlFor="username" className="login-label">
              {t('LoginPage.usernameOrEmailLabel')}
            </label>
            <div className="login-form-input-container">
              {usernameSVG}
              <input
                id="username"
                name="username"
                className={`login-input ${usernameFormatError ? 'error' : ''}`}
                placeholder={t('LoginPage.usernameOrEmailHint')}
                value={formData.username}
                onChange={handleInputChange}
                autoFocus
                required
              />
            </div>
            {usernameFormatError && <span className="login-error">{usernameFormatError}</span>}
            {errors.username && <span className="login-error">{errors.username}</span>}
          </div>

          <button
            type="submit"
            className="login-wizard-button"
            disabled={!formData.username.trim() || !!usernameFormatError || isLoading}
          >
            {isLoading ? t('LoginPage.resolving') : t('LoginPage.continue')}
          </button>
        </form>
      ) : (
        <form className="login-form" onSubmit={handleLogin}>
          <div className="login-form-group">
            <label htmlFor="username-display" className="login-label">
              {t('LoginPage.usernameOrEmailLabel')}
            </label>
            <div className="login-form-input-container">
              {usernameSVG}
              <input
                id="username-display"
                name="username"
                className="login-input"
                value={formData.username}
                disabled
                readOnly
              />
            </div>
          </div>
          
          <div className="login-form-group">
            <label htmlFor="password" className="login-label">
              {t('LoginPage.passwordInputLabel')}
            </label>
            <div className="login-form-input-container">
              {passwordSVG}
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                className={`login-input ${errors.password ? 'error' : ''}`}
                placeholder={t('LoginPage.passwordInputHint')}
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
                autoFocus
              />
              <button
                type="button"
                className="password-toggle-button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('LoginPage.hidePassword') : t('LoginPage.showPassword')}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#9b9b9b" width="20px" height="20px">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#9b9b9b" width="20px" height="20px">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <span className="login-error">{errors.password}</span>}
          </div>
          
          <a className="login-form-reset-password">
            {t('LoginPage.forgotPassword')}
          </a>
          
          <div className="login-wizard-button-row">
            <button 
              type="button" 
              className="login-wizard-button" 
              onClick={handleBack}
              disabled={isLoading}
            >
              {t('LoginPage.back')}
            </button>
            <button 
              type="submit" 
              className="login-wizard-button" 
              disabled={!formData.password || isLoading}
            >
              {isLoading ? t('LoginPage.signingIn') : t('LoginPage.continue')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default LoginForm;
